/**
 * 무빈소 "장례식장 찾기" 퍼널의 관심 장례식장 체크 화면(#?) 실험.
 *
 * 무엇을 하는 실험인가
 * - `/match/request` 무빈소 퍼널에서 Q1 을 "무빈소 장례가 가능한 장례식장을 찾고싶어요"
 *   로 고른 사람은 시도·시군구까지 답한다. 그 지역 장례식장 목록을 전환 화면 앞에 세우고
 *   궁금한 곳을 체크받는다. 체크한 이름은 리드 슬랙 메시지에 실려 상담사가 본다.
 * - A: 현행 3스텝(info1 → info2 → region → conversion)
 * - B: 4스텝(위 + 관심 장례식장 체크)
 *
 * #344(지역 무빈소)와 무엇이 다른가
 * - #344 는 **가입 후** 화면이라 `isMubinsoAvailableParlor` 로 "무빈소 가능"과
 *   "추가 확인이 필요한 곳"을 갈라 보여준다. 약속한 답을 이행하는 자리다.
 * - 이 실험은 **가입 전**이다. 여기서 가능 여부를 먼저 보여주면 리드를 낼 이유가 사라진다.
 *   그래서 판정을 쓰지 않고 전부 "확인 필요"로 적는다. 판정 데이터가 있는 곳은 1,073곳 중
 *   126곳(11.7%)뿐이라, 전부 확인 필요로 적어도 사실과 어긋나지 않는다.
 *
 * 알고 갈 것: 스텝이 하나 늘어난다
 * - #315(첫문항 니즈)가 리드 하락, #335·#337(롤백)이 B 승자로 끝나 2026-09 코드에서
 *   첫문항을 걷어냈다. 폼 앞에 질문을 더하는 것은 마찰이라는 사실은 여기도 같다.
 * - 다만 그때는 폼 **앞**이었고 이건 전환 화면 **직전**이며, 진입 의도가
 *   "장례식장을 찾고싶어요"인 사람에게 실제 장례식장 이름을 보여주는 것이라 메시지가
 *   한 축에 정렬된다. 그래도 B 의 제출률이 A 보다 낮게 나올 수 있다.
 */

/**
 * Hackle experimentKey.
 *
 * ⚠️ **배포 전에 콘솔의 실제 key 와 같은지 반드시 확인할 것.**
 *
 * 352 는 영민이 지정한 번호다(2026-09-18). 적는 시점에 콘솔에는 **아직 없었다.**
 * 조회 결과 최대 key 가 351, 전체 351건이었고 `searchKeyword: "352"` 는 0건이었다.
 * 즉 "다음에 만들 번호"를 미리 적은 상태다.
 *
 * 왜 이걸 굳이 적어 두는가
 * - 이것이 정확히 #333 사고의 형태다("조회 시점 최대 key 가 332라 333을 적었다").
 *   실험을 만들기 전에 **다른 사람이 먼저 하나 만들면 352 는 그쪽에 가고**, 이 코드는
 *   남의 실험 분모를 오염시킨다. 마운트되는 순간 그 키로 노출이 기록되기 때문이다.
 * - 그래서 콘솔에서 만든 뒤 번호가 352 가 맞는지 눈으로 확인하고, 다르면 여기를 고친다.
 *
 * 0 으로 되돌리면 어느 화면도 실험 컴포넌트를 마운트하지 않고 현행 3스텝으로 흐른다
 * (#344 와 같은 안전 기본값).
 */
export const MUBINSO_PARLOR_PICK_EXPERIMENT_KEY = 352;

/**
 * 실험 컨테이너를 마운트해도 되는지.
 *
 * 모수 판정(찾기 퍼널 진입인지)은 호출부가 이미 알고 있어 여기서 다시 묻지 않는다.
 * 이 함수가 답하는 것은 "키가 설정됐고, 세울 목록이 있는가" 둘이다.
 *
 * 목록이 비어 있으면 false 인 이유
 * - 페이지 밖에서 컨테이너를 쓰면 `parlorOptions` 가 빈 배열로 내려온다. 그대로
 *   마운트하면 B 배정자가 빈 화면을 보고 노출만 찍힌다.
 * - 시군구에 장례식장이 한 곳도 없는 경우도 있다(서울 마포구, 경기 과천시, 인천 옹진군,
 *   강원 양양군, 충남 계룡시 5곳). 그 판정은 지역을 고른 뒤에야 가능해 화면이 처리한다.
 *
 * @param hasOptions 세울 장례식장이 한 곳이라도 있는지
 */
export const shouldMountMubinsoParlorPickExperiment = (hasOptions: boolean): boolean =>
  MUBINSO_PARLOR_PICK_EXPERIMENT_KEY > 0 && hasOptions;
